import React, { useState, useCallback, useEffect } from 'react';
import { useGroupSessionStore } from '../../stores/prioritization/groupSessionStore';
import { GroupSessionStatusPill } from './GroupSessionStatusPill';

const sectionLabel: React.CSSProperties = {
  fontFamily: 'var(--mono)',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-ter)',
  marginBottom: '10px',
  display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg-raised)',
  border: '1px solid var(--rule-bright)',
  borderRadius: '6px',
  color: 'var(--text-pri)',
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  padding: '9px 12px',
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryBtn: React.CSSProperties = {
  background: 'var(--mustard)',
  color: '#0B0F19',
  border: 'none',
  borderRadius: '8px',
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  fontWeight: 700,
  padding: '10px 20px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const framingHeaderRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '24px',
  flexWrap: 'wrap',
};

const decisionTitleInput: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'var(--serif)',
  fontSize: '22px',
  fontWeight: 600,
  color: 'var(--text-pri)',
  lineHeight: 1.3,
  margin: '0 0 6px',
  padding: '4px 0',
  border: 'none',
  borderBottom: '2px solid var(--rule-bright)',
  borderRadius: 0,
  background: 'transparent',
  outline: 'none',
};

export function GroupDecisionFramingPanel({
  sessionId,
  toast,
}: {
  sessionId: string;
  toast: { show: (m: string) => void };
}) {
  const currentSession = useGroupSessionStore((s) => s.currentSession);
  const updateGroupSessionDetails = useGroupSessionStore((s) => s.updateGroupSessionDetails);
  const [titleDraft, setTitleDraft] = useState('');
  const [descDraft, setDescDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentSession || currentSession.id !== sessionId) return;
    setTitleDraft(currentSession.title);
    setDescDraft(currentSession.description ?? '');
  }, [sessionId, currentSession?.id, currentSession?.title, currentSession?.description]);

  const dirty =
    currentSession &&
    (titleDraft.trim() !== currentSession.title.trim() ||
      descDraft !== (currentSession.description ?? ''));

  const handleSave = useCallback(async () => {
    if (!currentSession || currentSession.id !== sessionId) return;
    const t = titleDraft.trim();
    if (!t) {
      toast.show('Add a decision question (title).');
      return;
    }
    setSaving(true);
    try {
      const r = await updateGroupSessionDetails(sessionId, t, descDraft);
      if (!r.ok) {
        toast.show(r.error);
        return;
      }
      toast.show('Saved decision framing.');
    } finally {
      setSaving(false);
    }
  }, [sessionId, currentSession, titleDraft, descDraft, updateGroupSessionDetails, toast]);

  if (!currentSession) return null;

  return (
    <div style={{ marginBottom: '28px' }}>
      <div style={framingHeaderRow}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ ...sectionLabel, marginBottom: '8px' }}>Decision question</span>
          <input
            style={decisionTitleInput}
            aria-label="Decision question"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            placeholder="What are you deciding?"
          />
          <label
            htmlFor="group-session-context"
            style={{ ...sectionLabel, marginBottom: '6px', marginTop: '16px', display: 'block' }}
          >
            Context (optional)
          </label>
          <textarea
            id="group-session-context"
            style={{
              ...inputStyle,
              minHeight: '88px',
              resize: 'vertical',
              marginBottom: '10px',
            }}
            placeholder="Why this decision matters, constraints, deadlines, links…"
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
          />
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
            style={{
              ...primaryBtn,
              padding: '8px 16px',
              fontSize: '13px',
              opacity: dirty && !saving ? 1 : 0.45,
              cursor: dirty && !saving ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Saving…' : 'Save decision framing'}
          </button>
        </div>
        <GroupSessionStatusPill status={currentSession.groupSessionStatus} />
      </div>
    </div>
  );
}

/** Short fixed-duration toast for group session pages. */
export function useGroupPageToast() {
  const [message, setMessage] = React.useState<string | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = React.useCallback((msg: string) => {
    setMessage(msg);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setMessage(null), 2500);
  }, []);

  return { message, show };
}
